/**
 * A aba de usuários do admin, portada de app.js:833-874 (`renderUsers`,
 * `renderUsersInner`, `userCard`, `bindUserCards`).
 *
 * Mesma ordem de leitura do painel atual: primeiro criar, que é o motivo de
 * alguém abrir esta aba, e depois a lista — cada pessoa com papel, alcance e os
 * projetos marcáveis no próprio cartão. As ações são as três que a API expõe
 * hoje (criar, salvar acesso, desativar); nenhuma delas pede confirmação no
 * painel atual, e continua assim.
 *
 * O que muda: salvar acesso passa a recarregar a lista. No painel atual a linha
 * `acesso: …` continuava mostrando o conjunto antigo até trocar de aba, o que
 * fazia parecer que o salvamento não tinha pegado.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { postar } from '../api/cliente';
import { chaves, useProjetosAdmin, useUsuarios } from '../api/consultas';
import type { ProjetoAdmin, Usuario } from '../api/tipos';
import { Carregando, Falha, Vazio } from '../componentes/Estado';
import { useRecado } from '../componentes/Recado';

type Papel = 'dev' | 'admin';

const mensagem = (e: unknown) => (e instanceof Error ? e.message : String(e));

// A aba é do admin, não de um projeto, e por isso ignora o slug da URL — a
// assinatura segue a das outras telas só para caber no mapa de Tela.tsx.
export function Usuarios({ slug: _slug }: { slug: string }) {
  const avisar = useRecado();
  const fila = useQueryClient();
  const usuarios = useUsuarios();
  const projetos = useProjetosAdmin();
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [papel, setPapel] = useState<Papel>('dev');

  const criar = useMutation({
    mutationFn: (corpo: { email: string; password: string; role: Papel }) =>
      postar<{ id?: string }>('/api/users', corpo),
  });

  const enviar = async () => {
    const limpo = email.trim();
    if (!limpo || senha.length < 6) { avisar('email + senha (6+) obrigatórios'); return; }
    try {
      await criar.mutateAsync({ email: limpo, password: senha, role: papel });
      avisar('usuário criado');
      // o painel atual redesenhava a aba inteira, e com isso o formulário
      // voltava ao estado inicial: campos vazios e papel em dev
      setEmail('');
      setSenha('');
      setPapel('dev');
      await fila.invalidateQueries({ queryKey: chaves.usuarios });
    } catch (e) { avisar(mensagem(e)); }
  };

  // as duas leituras vinham num Promise.all: sem os projetos não há grade de
  // acesso para desenhar, então a aba espera as duas antes de aparecer
  if (usuarios.isPending || projetos.isPending) return <Carregando rotulo="a lista de usuários" />;
  const erro = usuarios.error || projetos.error;
  if (erro) {
    return (
      <Falha
        erro={erro}
        rotulo="a lista de usuários"
        aoRepetir={() => { void usuarios.refetch(); void projetos.refetch(); }}
      />
    );
  }

  const lista = usuarios.data || [];
  const acervo = projetos.data || [];

  return (
    <>
      <div className="crumb">admin<span className="sep">/</span>usuários</div>

      <div className="sec-title">Novo usuário</div>
      <div className="sec-sub">
        Somente o admin cria usuários. Devs veem apenas os projetos delegados.
      </div>
      <div className="admin-grid">
        <div className="fld">
          <label htmlFor="u-new-email">Email</label>
          <input
            id="u-new-email"
            placeholder="dev@extreme.digital"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="fld">
          <label htmlFor="u-new-pass">Senha inicial</label>
          <input
            id="u-new-pass"
            className="mono"
            placeholder="mín. 6 caracteres"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
          />
        </div>
        <div className="fld">
          {/* o rótulo nomeia o grupo, e não um dos botões: apontar um `for` para
              o primeiro faria "Dev" se chamar "Papel" para o leitor de tela */}
          <label id="u-role-rotulo">Papel</label>
          <div className="seg" id="u-role-seg" role="group" aria-labelledby="u-role-rotulo">
            <button
              type="button"
              className={papel === 'dev' ? 'on' : ''}
              aria-pressed={papel === 'dev'}
              onClick={() => setPapel('dev')}
            >
              Dev
            </button>
            <button
              type="button"
              className={papel === 'admin' ? 'on' : ''}
              aria-pressed={papel === 'admin'}
              onClick={() => setPapel('admin')}
            >
              Admin
            </button>
          </div>
        </div>
      </div>
      <div style={{ marginTop: 16 }}>
        <button type="button" className="btn" onClick={() => void enviar()}>Criar usuário</button>
      </div>

      <div className="sec-title" style={{ marginTop: 34 }}>Usuários</div>
      {lista.length
        ? lista.map((u) => <Cartao key={u.id} usuario={u} projetos={acervo} />)
        : (
          <Vazio titulo="Nenhum usuário no acervo">
            Crie o primeiro acima: email e senha inicial bastam. Devs veem apenas os projetos
            delegados.
          </Vazio>
        )}
    </>
  );
}

function Cartao({ usuario, projetos }: { usuario: Usuario; projetos: ProjetoAdmin[] }) {
  const avisar = useRecado();
  const fila = useQueryClient();
  const ehAdmin = usuario.role === 'admin';
  const acesso = usuario.projects === '*' ? [] : (usuario.projects || []);
  const [marcados, setMarcados] = useState<string[]>(acesso);

  const salvarAcesso = useMutation({
    mutationFn: (escopo: string[]) => postar(`/api/users/${usuario.id}/access`, { projects: escopo }),
  });
  const desativar = useMutation({
    mutationFn: () => postar(`/api/users/${usuario.id}/deactivate`, {}),
  });

  const alternar = (slug: string) => setMarcados(
    (atual) => (atual.includes(slug) ? atual.filter((s) => s !== slug) : [...atual, slug]),
  );

  const salvar = async () => {
    // o envio sai na ordem da grade, e não na de clique: o painel atual lia os
    // checkboxes marcados na ordem do DOM
    const escopo = projetos.map((p) => p.slug).filter((s) => marcados.includes(s));
    try {
      await salvarAcesso.mutateAsync(escopo);
      avisar('acesso atualizado');
      await fila.invalidateQueries({ queryKey: chaves.usuarios });
    } catch (e) { avisar(mensagem(e)); }
  };

  const remover = async () => {
    try {
      await desativar.mutateAsync();
      avisar('usuário desativado');
      await fila.invalidateQueries({ queryKey: chaves.usuarios });
    } catch (e) { avisar(mensagem(e)); }
  };

  return (
    <div className="row-card" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span className="avatar">{(usuario.email[0] || '?').toUpperCase()}</span>
        <div style={{ flex: 1 }}>
          <div className="pname">
            {usuario.email} <span className={`pill ${usuario.role}`}>{usuario.role}</span>{' '}
            {!usuario.active && (
              <span
                className="pill"
                style={{ background: 'var(--surface-3)', color: 'var(--ink-faint)' }}
              >
                inativo
              </span>
            )}
          </div>
          <div className="pmeta">
            {ehAdmin
              ? 'vê todos os projetos'
              : `acesso: ${acesso.length ? acesso.join(', ') : 'nenhum projeto — marque abaixo e salve'}`}
          </div>
        </div>
        {/* admin não aparece com botão de desativar, e é o que impede o admin de
            se desligar sozinho e deixar o acervo sem ninguém que crie usuário */}
        {!!usuario.active && !ehAdmin && (
          <button type="button" className="btn ghost sm" onClick={() => void remover()}>
            Desativar
          </button>
        )}
      </div>
      {!ehAdmin && (
        <>
          <div className="access-grid">
            {projetos.length
              ? projetos.map((p) => (
                <label className="chk" key={p.slug}>
                  <input
                    type="checkbox"
                    value={p.slug}
                    checked={marcados.includes(p.slug)}
                    onChange={() => alternar(p.slug)}
                  />
                  {' '}{p.slug}
                </label>
              ))
              : <span className="muted">nenhum projeto no acervo</span>}
          </div>
          <div>
            <button type="button" className="btn sm" onClick={() => void salvar()}>
              Salvar acesso
            </button>
          </div>
        </>
      )}
    </div>
  );
}
